import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { format } from 'date-fns'
import {
  ArrowLeft,
  Save,
  Trash2,
  ChevronRight,
  Clock,
  User,
  Plus,
  Briefcase,
  Copy,
  Phone,
  MessageSquare,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Card, CardContent, CardTitle } from '@/components/ui/card'
import { CollapsibleCard } from '@/components/CollapsibleCard'
import { useIsMobile } from '@/hooks/useIsMobile'
import { Separator } from '@/components/ui/separator'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import { StatusBadge } from '@/components/StatusBadge'
import { TransitionDialog, type TransitionDialogResult } from '@/components/TransitionDialog'
import { projectSizeOptions } from '@/lib/leadOptions'
import { useLead, useUpdateLead, useDeleteLead, useTransitionLead } from '@/hooks/useLeads'
import { useLeadProjects, useCreateProject, useUpdateProject, useDeleteProject, useTransitionProject, projectKeys } from '@/hooks/useProjects'
import { useUsers } from '@/hooks/useUsers'
import { useActivityLogs } from '@/hooks/useActivity'
import { projectService } from '@/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import {
  LeadStatus,
  SourceChannel,
  ProjectSize,
  ProjectStatus,
  NextAction,
  LeadUpdate,
  ProjectCreate,
  ProjectUpdate,
  ProjectTransition,
  ProjectResponse,
  UserRole,
  EntityType,
} from '@/types/api'

const sourceOptions = [
  { value: SourceChannel.PHONE, label: 'Phone' },
  { value: SourceChannel.WALK_IN, label: 'Walk-in' },
  { value: SourceChannel.WEBSITE, label: 'Website' },
  { value: SourceChannel.REFERRAL, label: 'Referral' },
  { value: SourceChannel.GOOGLE_ADS, label: 'Google Ads' },
  { value: SourceChannel.FACEBOOK, label: 'Facebook' },
  { value: SourceChannel.INSTAGRAM, label: 'Instagram' },
  { value: SourceChannel.YELP, label: 'Yelp' },
  { value: SourceChannel.RECURRING_CLIENT, label: 'Recurring Client' },
  { value: SourceChannel.OTHER, label: 'Other' },
]

const nextActionOptions = [
  { value: NextAction.CALL, label: 'Call' },
  { value: NextAction.EMAIL, label: 'Email' },
  { value: NextAction.SCHEDULE_ESTIMATE, label: 'Schedule Estimate' },
  { value: NextAction.SEND_QUOTE, label: 'Send Quote' },
  { value: NextAction.FOLLOW_UP, label: 'Follow Up' },
  { value: NextAction.OTHER, label: 'Other' },
]

const statusFlow: LeadStatus[] = [
  LeadStatus.NEW,
  LeadStatus.CONTACTED,
  LeadStatus.ESTIMATE_SCHEDULED,
  LeadStatus.ESTIMATE_SENT,
  LeadStatus.WON,
]

const projectStatusFlow: ProjectStatus[] = [
  ProjectStatus.NEW,
  ProjectStatus.ESTIMATE_SCHEDULED,
  ProjectStatus.ESTIMATE_SENT,
  ProjectStatus.WON,
]

export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const { user } = useAuthStore()
  const isAdmin = user?.role === UserRole.ADMIN

  const { data: lead, isLoading } = useLead(id!)
  const { data: users } = useUsers()
  const { data: activityLogs } = useActivityLogs({
    entity_type: EntityType.LEAD,
    entity_id: id,
    limit: 50,
  })
  const { data: projectsData } = useLeadProjects(id!)

  const queryClient = useQueryClient()
  const updateMutation = useUpdateLead(id!)
  const deleteMutation = useDeleteLead()
  const transitionMutation = useTransitionLead(id!)
  const createProjectMutation = useCreateProject()
  const projectTransitionMutation = useMutation({
    mutationFn: ({ projectId, data }: { projectId: string; data: ProjectTransition }) =>
      projectService.transitionProject(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectKeys.byLead(id!) })
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() })
    },
  })

  const [formData, setFormData] = useState<LeadUpdate>({})
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showTransitionDialog, setShowTransitionDialog] = useState(false)
  const [transitionTarget, setTransitionTarget] = useState<LeadStatus | null>(null)

  // Project-related state
  const [showAddProjectInline, setShowAddProjectInline] = useState(false)
  const [newProjectData, setNewProjectData] = useState<Partial<ProjectCreate>>({})
  const [showProjectTransitionDialog, setShowProjectTransitionDialog] = useState(false)
  const [projectTransitionTarget, setProjectTransitionTarget] = useState<{ projectId: string; status: ProjectStatus } | null>(null)
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null)
  const [editProjectData, setEditProjectData] = useState<ProjectUpdate>({})

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!lead) {
    return (
      <div className="flex h-64 flex-col items-center justify-center">
        <p className="text-lg text-muted-foreground">Lead not found</p>
        <Button variant="link" onClick={() => navigate('/leads')}>
          Back to Leads
        </Button>
      </div>
    )
  }

  const handleChange = (
    field: keyof LeadUpdate,
    value: string | number | undefined
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const getValue = <K extends keyof LeadUpdate>(field: K): LeadUpdate[K] => {
    return formData[field] !== undefined
      ? formData[field]
      : (lead[field as keyof typeof lead] as LeadUpdate[K])
  }

  const handleSave = () => {
    if (Object.keys(formData).length > 0) {
      updateMutation.mutate(formData)
    }
  }

  const handleDelete = () => {
    deleteMutation.mutate(lead.id, {
      onSuccess: () => navigate('/leads'),
    })
  }

  const initiateTransition = (status: LeadStatus) => {
    setTransitionTarget(status)
    if (status === LeadStatus.LOST || status === LeadStatus.WON) {
      setShowTransitionDialog(true)
    } else {
      transitionMutation.mutate({ new_status: status })
    }
  }

  const confirmTransition = (result: TransitionDialogResult) => {
    if (!transitionTarget) return

    if (transitionTarget === LeadStatus.LOST) {
      transitionMutation.mutate({
        new_status: transitionTarget,
        disqualification_reason: result.disqualificationReason,
      })
    } else if (transitionTarget === LeadStatus.WON) {
      transitionMutation.mutate({
        new_status: transitionTarget,
        project_size: result.projectSize,
      })
    }
  }

  const currentStatusIndex = statusFlow.indexOf(lead.lead_status)
  const nextStatus =
    currentStatusIndex >= 0 && currentStatusIndex < statusFlow.length - 1
      ? statusFlow[currentStatusIndex + 1]
      : null

  // Project handlers
  const handleAddProject = () => {
    if (!id) return
    createProjectMutation.mutate(
      { ...newProjectData, lead_id: id } as ProjectCreate,
      {
        onSuccess: () => {
          setShowAddProjectInline(false)
          setNewProjectData({})
        },
      }
    )
  }

  const initiateProjectTransition = (projectId: string, status: ProjectStatus) => {
    setProjectTransitionTarget({ projectId, status })
    if (status === ProjectStatus.LOST || status === ProjectStatus.WON) {
      setShowProjectTransitionDialog(true)
    } else {
      // Direct transition
      projectTransitionMutation.mutate({ projectId, data: { new_status: status } })
    }
  }

  const confirmProjectTransition = (result: TransitionDialogResult) => {
    if (!projectTransitionTarget) return

    const { projectId, status } = projectTransitionTarget

    if (status === ProjectStatus.LOST) {
      projectTransitionMutation.mutate({
        projectId,
        data: { new_status: status, disqualification_reason: result.disqualificationReason },
      })
    } else if (status === ProjectStatus.WON) {
      projectTransitionMutation.mutate({
        projectId,
        data: { new_status: status, project_size: result.projectSize },
      })
    }
  }

  const getProjectNextStatus = (project: ProjectResponse): ProjectStatus | null => {
    const idx = projectStatusFlow.indexOf(project.project_status as ProjectStatus)
    if (idx >= 0 && idx < projectStatusFlow.length - 1) {
      return projectStatusFlow[idx + 1]
    }
    return null
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/leads')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {lead.first_name} {lead.last_name}
            </h1>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <StatusBadge status={lead.lead_status} />
              <span>Created {format(new Date(lead.created_at), 'PPP')}</span>
            </div>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
          {isAdmin && (
            <Button
              variant="destructive"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
          <Button onClick={handleSave} disabled={updateMutation.isPending}>
            <Save className="mr-2 h-4 w-4" />
            Save Changes
          </Button>
        </div>
      </div>

      {/* Mobile Call / Text buttons */}
      {isMobile && (
        <div className="flex gap-3">
          <Button
            className="flex-1"
            variant="default"
            asChild
          >
            <a href={`tel:${lead.phone || ''}`}>
              <Phone className="mr-2 h-4 w-4" />
              Call
            </a>
          </Button>
          <Button
            className="flex-1"
            variant="info"
            asChild
          >
            <a href={`sms:${lead.phone || ''}`}>
              <MessageSquare className="mr-2 h-4 w-4" />
              Text
            </a>
          </Button>
        </div>
      )}

      {/* Stage Transition Buttons */}
      {lead.lead_status !== LeadStatus.WON &&
        lead.lead_status !== LeadStatus.LOST && (
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-wrap items-center gap-2 md:gap-4">
                <span className="text-sm font-medium">Move to:</span>
                {nextStatus && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => initiateTransition(nextStatus)}
                  >
                    {nextStatus.replace('_', ' ')}
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                )}
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => initiateTransition(LeadStatus.LOST)}
                >
                  Mark as Lost
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

      {(() => {
        const contactCard = (
          <CollapsibleCard
            id="card-contact"
            headerContent={<CardTitle>Contact Information</CardTitle>}
          >
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name</Label>
                  <Input
                    id="first_name"
                    value={getValue('first_name') || ''}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name</Label>
                  <Input
                    id="last_name"
                    value={getValue('last_name') || ''}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={getValue('email') || ''}
                    onChange={(e) => handleChange('email', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email_other">Email (Other)</Label>
                  <Input
                    id="email_other"
                    type="email"
                    value={getValue('email_other') || ''}
                    onChange={(e) => handleChange('email_other', e.target.value)}
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={getValue('phone') || ''}
                    onChange={(e) => handleChange('phone', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone_other">Phone (Other)</Label>
                  <Input
                    id="phone_other"
                    value={getValue('phone_other') || ''}
                    onChange={(e) => handleChange('phone_other', e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name</Label>
                <Input
                  id="company_name"
                  value={getValue('company_name') || ''}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                />
              </div>
              <Separator />
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  value={getValue('address') || ''}
                  onChange={(e) => handleChange('address', e.target.value)}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={getValue('city') || ''}
                    onChange={(e) => handleChange('city', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zip">ZIP Code</Label>
                  <Input
                    id="zip"
                    value={getValue('zip') || ''}
                    onChange={(e) => handleChange('zip', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </CollapsibleCard>
        )

        const leadInfoCard = (
          <CollapsibleCard
            id="card-lead-info"
            headerContent={<CardTitle>Lead Information</CardTitle>}
          >
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Source Channel</Label>
                  <Select
                    value={getValue('source_channel') || lead.source_channel}
                    onValueChange={(v) =>
                      handleChange('source_channel', v as SourceChannel)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {sourceOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="source_campaign">Campaign</Label>
                  <Input
                    id="source_campaign"
                    value={getValue('source_campaign') || ''}
                    onChange={(e) =>
                      handleChange('source_campaign', e.target.value)
                    }
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="referrer_name">Referrer Name</Label>
                  <Input
                    id="referrer_name"
                    value={getValue('referrer_name') || ''}
                    onChange={(e) =>
                      handleChange('referrer_name', e.target.value)
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lead_score">Lead Score (0-100)</Label>
                  <Input
                    id="lead_score"
                    type="number"
                    min={0}
                    max={100}
                    value={getValue('lead_score') ?? ''}
                    onChange={(e) =>
                      handleChange(
                        'lead_score',
                        e.target.value ? parseInt(e.target.value, 10) : undefined
                      )
                    }
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Project Size</Label>
                <Select
                  value={getValue('project_size') || ''}
                  onValueChange={(v) =>
                    handleChange('project_size', v as ProjectSize)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent>
                    {projectSizeOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pain_points">Pain Points</Label>
                <Textarea
                  id="pain_points"
                  value={getValue('pain_points') || ''}
                  onChange={(e) => handleChange('pain_points', e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          </CollapsibleCard>
        )

        const actionsCard = (
          <CollapsibleCard
            id="card-actions"
            headerContent={<CardTitle>Actions & Notes</CardTitle>}
          >
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Assigned To</Label>
                  <Select
                    value={getValue('assigned_to') || lead.assigned_to}
                    onValueChange={(v) => handleChange('assigned_to', v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {users?.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.username}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Next Action</Label>
                  <Select
                    value={getValue('next_action') || ''}
                    onValueChange={(v) =>
                      handleChange('next_action', v as NextAction)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select action" />
                    </SelectTrigger>
                    <SelectContent>
                      {nextActionOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="next_action_date">Next Action Date</Label>
                <Input
                  id="next_action_date"
                  type="date"
                  value={getValue('next_action_date') || ''}
                  onChange={(e) =>
                    handleChange('next_action_date', e.target.value)
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={getValue('notes') || ''}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  rows={4}
                />
              </div>

              {lead.lead_status === LeadStatus.LOST &&
                lead.disqualification_reason && (
                  <div className="rounded-lg border border-error bg-error/10 p-2 md:p-4">
                    <Label className="text-error">Disqualification Reason</Label>
                    <p className="mt-1 text-sm">
                      {lead.disqualification_reason}
                    </p>
                  </div>
                )}
            </div>
          </CollapsibleCard>
        )

        const projectsCard = (
          <CollapsibleCard
            id="card-projects"
            defaultExpanded
            headerContent={
              <div className="flex flex-row items-center justify-between w-full">
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Projects
                </CardTitle>
                <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                  {showAddProjectInline && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setNewProjectData((prev) => ({
                          ...prev,
                          address: lead.address || '',
                          city: lead.city || '',
                          zip: lead.zip || '',
                        }))
                      }}
                    >
                      <Copy className="mr-1 h-3 w-3" />
                      Copy Lead Address
                    </Button>
                  )}
                  {!showAddProjectInline && (
                    <Button
                      size="sm"
                      onClick={() => setShowAddProjectInline(true)}
                    >
                      <Plus className="mr-1 h-4 w-4" />
                      Add Project
                    </Button>
                  )}
                </div>
              </div>
            }
            headerClassName="flex flex-row items-center justify-between"
          >
              {showAddProjectInline && (
                <div className="rounded-lg border p-2 md:p-4 space-y-3 mb-4">
                  <div className="text-sm font-medium">New Project</div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Address</Label>
                      <Input
                        value={newProjectData.address || ''}
                        onChange={(e) => setNewProjectData((prev) => ({ ...prev, address: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">City</Label>
                      <Input
                        value={newProjectData.city || ''}
                        onChange={(e) => setNewProjectData((prev) => ({ ...prev, city: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="space-y-1">
                      <Label className="text-xs">ZIP</Label>
                      <Input
                        value={newProjectData.zip || ''}
                        onChange={(e) => setNewProjectData((prev) => ({ ...prev, zip: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Cost ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={newProjectData.project_cost ?? ''}
                        onChange={(e) => setNewProjectData((prev) => ({ ...prev, project_cost: e.target.value ? parseFloat(e.target.value) : undefined }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Estimate #</Label>
                      <Input
                        type="number"
                        value={newProjectData.estimate ?? ''}
                        onChange={(e) => setNewProjectData((prev) => ({ ...prev, estimate: e.target.value ? parseInt(e.target.value, 10) : undefined }))}
                      />
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Invoice #</Label>
                      <Input
                        type="number"
                        value={newProjectData.invoice ?? ''}
                        onChange={(e) => setNewProjectData((prev) => ({ ...prev, invoice: e.target.value ? parseInt(e.target.value, 10) : undefined }))}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Next Action</Label>
                      <Select
                        value={newProjectData.next_action || ''}
                        onValueChange={(v) => setNewProjectData((prev) => ({ ...prev, next_action: v as NextAction }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select action" />
                        </SelectTrigger>
                        <SelectContent>
                          {nextActionOptions.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Next Action Date</Label>
                      <Input
                        type="date"
                        value={newProjectData.next_action_date || ''}
                        onChange={(e) => setNewProjectData((prev) => ({ ...prev, next_action_date: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Pain Points</Label>
                    <Textarea
                      value={newProjectData.pain_points || ''}
                      onChange={(e) => setNewProjectData((prev) => ({ ...prev, pain_points: e.target.value }))}
                      rows={2}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Notes</Label>
                    <Textarea
                      value={newProjectData.notes || ''}
                      onChange={(e) => setNewProjectData((prev) => ({ ...prev, notes: e.target.value }))}
                      rows={2}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleAddProject} disabled={createProjectMutation.isPending}>
                      <Save className="mr-1 h-3 w-3" />
                      Create
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setShowAddProjectInline(false); setNewProjectData({}) }}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
              {projectsData?.items && projectsData.items.length > 0 ? (
                <div className="space-y-4">
                  {projectsData.items.map((project) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      isEditing={editingProjectId === project.id}
                      editData={editProjectData}
                      onStartEdit={() => {
                        setEditingProjectId(project.id)
                        setEditProjectData({})
                      }}
                      onCancelEdit={() => {
                        setEditingProjectId(null)
                        setEditProjectData({})
                      }}
                      onEditChange={setEditProjectData}
                      onTransition={(status) => initiateProjectTransition(project.id, status)}
                      nextStatus={getProjectNextStatus(project)}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-6">
                  No projects yet. Click &quot;Add Project&quot; to create one.
                </p>
              )}
          </CollapsibleCard>
        )

        const activityCard = (
          <CollapsibleCard
            id="card-activity"
            headerContent={<CardTitle>Activity Timeline</CardTitle>}
            cardClassName={!isMobile ? 'lg:sticky lg:top-20' : undefined}
          >
            <div className="space-y-4 max-h-[600px] overflow-y-auto">
              {activityLogs?.items.map((log) => (
                <div
                  key={log.id}
                  className="flex gap-3 border-l-2 border-muted pl-4 pb-4"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm">
                      <User className="h-3 w-3 text-muted-foreground" />
                      <span className="font-medium">
                        {log.username || 'System'}
                      </span>
                      <span className="capitalize text-muted-foreground">
                        {log.action}
                      </span>
                    </div>
                    {log.description && (
                      <p className="mt-1 text-sm text-muted-foreground">
                        {log.description}
                      </p>
                    )}
                    <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {format(new Date(log.created_at), 'PPp')}
                    </div>
                  </div>
                </div>
              ))}
              {(!activityLogs?.items || activityLogs.items.length === 0) && (
                <p className="text-sm text-muted-foreground">
                  No activity recorded yet
                </p>
              )}
            </div>
          </CollapsibleCard>
        )

        return isMobile ? (
          <div className="space-y-3">
            {projectsCard}
            {contactCard}
            {leadInfoCard}
            {actionsCard}
            {activityCard}
            {/* Mobile bottom action buttons */}
            <div className="flex items-center gap-2 pt-1">
              {isAdmin && (
                <Button
                  variant="destructive"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              )}
              <Button onClick={handleSave} disabled={updateMutation.isPending}>
                <Save className="mr-2 h-4 w-4" />
                Save Changes
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="space-y-3 lg:col-span-2">
              {contactCard}
              {leadInfoCard}
              {actionsCard}
              {projectsCard}
            </div>
            <div>
              {activityCard}
            </div>
          </div>
        )
      })()}

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Lead</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this lead? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead Transition Dialog */}
      <TransitionDialog
        open={showTransitionDialog}
        onOpenChange={setShowTransitionDialog}
        mode={transitionTarget === LeadStatus.WON ? 'won' : 'lost'}
        isPending={transitionMutation.isPending}
        onConfirm={confirmTransition}
      />

      {/* Project Transition Dialog */}
      <TransitionDialog
        open={showProjectTransitionDialog}
        onOpenChange={setShowProjectTransitionDialog}
        mode={projectTransitionTarget?.status === ProjectStatus.WON ? 'won' : 'lost'}
        entityLabel="Project"
        isPending={projectTransitionMutation.isPending}
        onConfirm={confirmProjectTransition}
      />
    </div>
  )
}


/**
 * Individual project card component
 */
function ProjectCard({
  project,
  isEditing,
  editData,
  onStartEdit,
  onCancelEdit,
  onEditChange,
  onTransition,
  nextStatus,
}: {
  project: ProjectResponse
  isEditing: boolean
  editData: ProjectUpdate
  onStartEdit: () => void
  onCancelEdit: () => void
  onEditChange: (data: ProjectUpdate) => void
  onTransition: (status: ProjectStatus) => void
  nextStatus: ProjectStatus | null
}) {
  const updateMutation = useUpdateProject(project.id)
  const deleteMutation = useDeleteProject()
  const transitionMutation = useTransitionProject(project.id)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

  const handleSaveEdit = () => {
    if (Object.keys(editData).length > 0) {
      updateMutation.mutate(editData, { onSuccess: onCancelEdit })
    } else {
      onCancelEdit()
    }
  }

  const handleDirectTransition = (status: ProjectStatus) => {
    transitionMutation.mutate({ new_status: status })
  }

  const isTerminal = project.project_status === ProjectStatus.WON || project.project_status === ProjectStatus.LOST

  return (
    <div className="rounded-lg border p-2 md:p-4 space-y-3">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {project.estimate != null && (
            <span className="text-base font-semibold">{project.estimate}</span>
          )}
          {project.address && (
            <span className="text-sm font-medium">{project.address}</span>
          )}
          {project.city && (
            <span className="text-sm text-muted-foreground">{project.city}</span>
          )}
          <StatusBadge status={project.project_status as ProjectStatus} />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!isTerminal && nextStatus && (
            <>
              {nextStatus === ProjectStatus.WON || nextStatus === ProjectStatus.LOST ? (
                <Button size="sm" variant="outline" onClick={() => onTransition(nextStatus)}>
                  {nextStatus === ProjectStatus.WON ? 'Won' : 'Lost'}
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={() => handleDirectTransition(nextStatus)}>
                  {nextStatus.replace('_', ' ')}
                  <ChevronRight className="ml-1 h-3 w-3" />
                </Button>
              )}
              <Button size="sm" variant="destructive" onClick={() => onTransition(ProjectStatus.LOST)}>
                Lost
              </Button>
            </>
          )}
          {!isEditing && (
            <Button size="sm" variant="ghost" onClick={onStartEdit}>
              Edit
            </Button>
          )}
        </div>
      </div>

      {/* Project details */}
      <div className="grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
        {project.project_size && (
          <div>
            <span className="text-muted-foreground">Size: </span>
            <span className="capitalize">{project.project_size}</span>
          </div>
        )}
        {project.project_cost != null && (
          <div>
            <span className="text-muted-foreground">Cost: </span>
            <span>${project.project_cost.toFixed(2)}</span>
          </div>
        )}
        {project.estimate != null && (
          <div>
            <span className="text-muted-foreground">Estimate #: </span>
            <span>{project.estimate}</span>
          </div>
        )}
        {project.invoice != null && (
          <div>
            <span className="text-muted-foreground">Invoice #: </span>
            <span>{project.invoice}</span>
          </div>
        )}
        {project.next_action && (
          <div>
            <span className="text-muted-foreground">Next: </span>
            <span className="capitalize">{project.next_action.replace('_', ' ')}</span>
          </div>
        )}
        {project.next_action_date && (
          <div>
            <span className="text-muted-foreground">Due: </span>
            <span>{project.next_action_date}</span>
          </div>
        )}
      </div>

      {project.disqualification_reason && (
        <div className="rounded border border-error bg-error/10 p-2 text-sm">
          <span className="font-medium text-error">Lost reason: </span>
          {project.disqualification_reason}
        </div>
      )}

      {project.notes && !isEditing && (
        <p className="text-sm text-muted-foreground">{project.notes}</p>
      )}

      <div className="text-xs text-muted-foreground">
        Created {format(new Date(project.created_at), 'PPP')}
      </div>

      {/* Inline edit form */}
      {isEditing && (
        <div className="space-y-3 border-t pt-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">Address</Label>
              <Input
                value={editData.address ?? project.address ?? ''}
                onChange={(e) => onEditChange({ ...editData, address: e.target.value })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">City</Label>
              <Input
                value={editData.city ?? project.city ?? ''}
                onChange={(e) => onEditChange({ ...editData, city: e.target.value })}
              />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label className="text-xs">Cost ($)</Label>
              <Input
                type="number"
                step="0.01"
                value={editData.project_cost ?? project.project_cost ?? ''}
                onChange={(e) => onEditChange({ ...editData, project_cost: e.target.value ? parseFloat(e.target.value) : undefined })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Estimate #</Label>
              <Input
                type="number"
                value={editData.estimate ?? project.estimate ?? ''}
                onChange={(e) => onEditChange({ ...editData, estimate: e.target.value ? parseInt(e.target.value, 10) : undefined })}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Invoice #</Label>
              <Input
                type="number"
                value={editData.invoice ?? project.invoice ?? ''}
                onChange={(e) => onEditChange({ ...editData, invoice: e.target.value ? parseInt(e.target.value, 10) : undefined })}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Notes</Label>
            <Textarea
              value={editData.notes ?? project.notes ?? ''}
              onChange={(e) => onEditChange({ ...editData, notes: e.target.value })}
              rows={2}
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSaveEdit} disabled={updateMutation.isPending}>
              <Save className="mr-1 h-3 w-3" />
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={onCancelEdit}>
              Cancel
            </Button>
            <Button size="sm" variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
              <Trash2 className="mr-1 h-3 w-3" />
              Delete
            </Button>
          </div>
        </div>
      )}

      {/* Delete Project Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Project</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this project? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate(project.id, { onSuccess: () => setShowDeleteConfirm(false) })}
              disabled={deleteMutation.isPending}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
